import { queryOptions } from "@tanstack/react-query";

import type {
  AdminDashboard,
  AdminOrganizationDetail,
  AdminOrganizationListResponse,
  UsageMetrics,
  UsageTrend,
} from "@kansai-ai-hub/api-client";
import type {
  AdminOrganizationListQuery,
  UsageMetricsQuery,
  UsageTrendQuery,
} from "@kansai-ai-hub/schema";

import { apiClient } from "@/lib/api-client";

export const adminKeys = {
  all: ["admin"] as const,
  dashboard: () => [...adminKeys.all, "dashboard"] as const,
  usageMetrics: (q: UsageMetricsQuery) => [...adminKeys.all, "usage-metrics", q] as const,
  usageTrend: (q: UsageTrendQuery) => [...adminKeys.all, "usage-trend", q] as const,
  organizations: () => [...adminKeys.all, "organizations"] as const,
  organizationList: (q: AdminOrganizationListQuery) =>
    [...adminKeys.organizations(), "list", q] as const,
  organization: (id: string) => [...adminKeys.organizations(), id] as const,
} as const;

export function adminDashboardQueryOptions() {
  return queryOptions({
    queryKey: adminKeys.dashboard(),
    queryFn: () => apiClient.get<AdminDashboard>("/api/v1/admin/dashboard"),
    staleTime: 30_000,
  });
}

export function adminUsageMetricsQueryOptions(query: UsageMetricsQuery) {
  return queryOptions({
    queryKey: adminKeys.usageMetrics(query),
    queryFn: () =>
      apiClient.get<UsageMetrics>("/api/v1/admin/usage-metrics", {
        query: query as unknown as Record<string, string | number | undefined>,
      }),
    staleTime: 60_000,
    // 集計条件の切替時に前回値を保持してチラつきを防ぐ。
    placeholderData: (previous) => previous,
  });
}

/**
 * 推移の 1 区間に、どの条件（range / role）で集計したかを付けたもの。
 * レスポンスには無いため取得時に付け、切替中に残る前のデータ（placeholderData）でも描画・ラベルをそのデータの条件に揃えられるようにする。
 */
export type ShownUsageTrend = UsageTrend & Pick<UsageTrendQuery, "range" | "role">;

export function adminUsageTrendQueryOptions(query: UsageTrendQuery) {
  return queryOptions({
    queryKey: adminKeys.usageTrend(query),
    queryFn: async (): Promise<ShownUsageTrend> => ({
      ...(await apiClient.get<UsageTrend>("/api/v1/admin/usage-metrics/trend", {
        query: query as unknown as Record<string, string | number | undefined>,
      })),
      range: query.range,
      role: query.role,
    }),
    staleTime: 60_000,
    // 矢印で区間を移る間、前の区間のグラフを残してチラつきを防ぐ。
    // 残したデータは前の条件のものなので、描画・ラベルは data.range / data.role に従う（props ではなく）。
    placeholderData: (previous) => previous,
  });
}

/**
 * role ごとに「UI に出していない絞り込み」を落として正規化する。
 *
 * 企業タブはクイックフィルタ (filter) を、会員タブは業界フィルタ (industries) を描画しないため、
 * 旧ブックマーク等で URL に残ったパラメータが裏で効いてしまうのを防ぐ。
 * route の loader と画面の双方がこの関数を通ることで queryKey が一致し、
 * 先読みしたキャッシュが捨てられて二重フェッチになるのも防げる。
 */
export function normalizeAdminOrganizationListQuery(
  query: AdminOrganizationListQuery,
): AdminOrganizationListQuery {
  const isEnterprise = query.role === "ENTERPRISE";
  return {
    ...query,
    filter: isEnterprise ? "all" : query.filter,
    industries: isEnterprise ? query.industries : undefined,
  };
}

export function adminOrganizationListQueryOptions(query: AdminOrganizationListQuery) {
  const normalized = normalizeAdminOrganizationListQuery(query);
  return queryOptions({
    queryKey: adminKeys.organizationList(normalized),
    queryFn: () =>
      apiClient.get<AdminOrganizationListResponse>("/api/v1/admin/organizations", {
        query: normalized as unknown as Record<string, string | number | undefined>,
      }),
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });
}

export function adminOrganizationQueryOptions(id: string) {
  return queryOptions({
    queryKey: adminKeys.organization(id),
    queryFn: () => apiClient.get<AdminOrganizationDetail>(`/api/v1/admin/organizations/${id}`),
    staleTime: 30_000,
  });
}
